export interface CsvTable {
  /** At most `maxRows` rows of at most `maxColumns` cells, the header row first. */
  rows: string[][];
  totalRows: number;
  totalColumns: number;
}

/**
 * Comma-separated values, RFC 4180 style: quoted fields may hold commas, line breaks and doubled
 * quotes; lines end in CRLF or LF. Every row is counted; only the first ones are kept, and a cell
 * longer than `maxChars` is cut with an ellipsis. Blank lines at the end are no rows.
 */
export function parseCsv(
  text: string,
  maxRows: number,
  maxColumns: number,
  maxChars: number
): CsvTable {
  const rows: string[][] = [];
  let totalRows = 0;
  let totalColumns = 0;
  let row: string[] = [];
  let columns = 0;
  let field = "";
  let cut = false;
  let quoted = false;
  // Whether the current line has begun: a last line without a line break still counts.
  let begun = false;
  // Blank lines not yet kept: rows only if a line with something on it follows.
  let blank = 0;

  const add = (char: string) => {
    if (field.length < maxChars) field += char;
    else cut = true;
  };
  const endField = () => {
    if (totalRows < maxRows && columns < maxColumns) row.push(cut ? `${field}…` : field);
    columns++;
    field = "";
    cut = false;
  };
  const endRow = () => {
    endField();
    totalColumns = Math.max(totalColumns, columns);
    if (totalRows < maxRows) rows.push(row);
    totalRows++;
    row = [];
    columns = 0;
    begun = false;
  };

  for (let i = text.charCodeAt(0) === 0xfeff ? 1 : 0; i < text.length; i++) {
    const char = text[i];
    if (quoted) {
      if (char !== '"') add(char);
      else if (text[i + 1] === '"') {
        add('"');
        i++;
      } else quoted = false;
      continue;
    }
    if (char === "\n" || char === "\r") {
      if (char === "\r" && text[i + 1] === "\n") i++;
      if (begun) endRow();
      else blank++;
      continue;
    }
    if (!begun) {
      for (; blank > 0; blank--) endRow();
      begun = true;
    }
    if (char === '"' && field === "") quoted = true;
    else if (char === ",") endField();
    else add(char);
  }
  if (begun) endRow();
  return { rows, totalRows, totalColumns };
}
